import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useInitiateUpload } from "@/hooks/useContentUpload";
import * as Sentry from "@sentry/react";
import { AlertCircle, CheckCircle2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";

export interface UploadData {
  uploadId: string;
  key: string;
  parts: Array<{ PartNumber: number; ETag: string }>;
}

interface MediaUploaderProps {
  type: "audio" | "video";
  onUploadProgress: (progress: number) => void;
  onUploadSuccess: (data: UploadData) => void;
  onUploadError: (error: Error) => void;
}

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

export function MediaUploader({
  type,
  onUploadProgress,
  onUploadSuccess,
  onUploadError,
}: MediaUploaderProps) {
  const [uploadState, setUploadState] = useState<
    "idle" | "uploading" | "completed" | "error"
  >("idle");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentProgress, setCurrentProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: initiateUpload } = useInitiateUpload(type);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isValidType =
      type === "audio"
        ? file.type.startsWith("audio/")
        : file.type.startsWith("video/");

    if (!isValidType) {
      setUploadState("error");
      onUploadError(new Error(`Please select a valid ${type} file`));
      return;
    }

    // Validate file size (500MB max)
    if (file.size > 500 * 1024 * 1024) {
      setUploadState("error");
      onUploadError(new Error("File size must be less than 500MB"));
      return;
    }

    setSelectedFile(file);
    handleUpload(file);
  };

  const handleUpload = async (file: File) => {
    setUploadState("uploading");
    setCurrentProgress(0);

    try {
      // Step 1: Initiate upload and get presigned URLs
      const response = await initiateUpload({
        fileName: file.name,
        contentType:
          file.type || (type === "audio" ? "audio/mpeg" : "video/mp4"),
        fileSize: file.size,
      });

      // Step 2: Upload file parts to S3
      const parts: Array<{ PartNumber: number; ETag: string }> = [];
      const totalParts = response.partUrls.length;

      for (let i = 0; i < totalParts; i++) {
        const partUrl = response.partUrls[i];
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        // Upload chunk to presigned URL
        const uploadResponse = await fetch(partUrl.url, {
          method: "PUT",
          body: chunk,
          headers: {
            "Content-Type": file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload part ${partUrl.partNumber}`);
        }

        // Get ETag from response headers
        const etag = uploadResponse.headers.get("ETag");
        if (!etag) {
          throw new Error(`No ETag returned for part ${partUrl.partNumber}`);
        }

        parts.push({
          PartNumber: partUrl.partNumber,
          ETag: etag.replace(/"/g, ""), // Remove quotes from ETag
        });

        // Update progress
        const progress = Math.round(((i + 1) / totalParts) * 100);
        setCurrentProgress(progress);
        onUploadProgress(progress);
      }

      // Step 3: Store upload metadata (don't call complete-upload yet!)
      const metadata: UploadData = {
        uploadId: response.uploadId,
        key: response.key,
        parts,
      };

      setUploadState("completed");
      onUploadSuccess(metadata);
      useInitiateUpload;
    } catch (error) {
      console.error("Upload error:", error);
      Sentry.captureException(error, {
        tags: {
          source: "MediaUploader.handleUpload",
          stage: "submit",
        },
        extra: {
          method: "PUT",
          url_path: `/${type}/initiate-upload`,
          mediaType: type,
          fileSize: file.size,
          fileMimeType: file.type,
          progress: currentProgress,
        },
      });
      setUploadState("error");
      onUploadError(
        error instanceof Error ? error : new Error("Upload failed")
      );
    }
  };

  const handleReset = () => {
    setUploadState("idle");
    setSelectedFile(null);
    setCurrentProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (uploadState === "completed" && selectedFile) {
    return (
      <div className='p-4 bg-green-50 border border-green-200 rounded-lg'>
        <div className='flex items-start gap-3'>
          <CheckCircle2 className='h-5 w-5 text-green-600 mt-0.5 shrink-0' />
          <div className='flex-1 min-w-0'>
            <p className='text-green-700 font-medium'>Upload complete!</p>
            <p className='text-sm text-green-600 mt-1 truncate'>
              {selectedFile.name}
            </p>
            <p className='text-xs text-green-600 mt-1'>
              Ready to create content. Fill in the details and click Create.
            </p>
          </div>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={handleReset}
            className='shrink-0'
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      </div>
    );
  }

  if (uploadState === "error") {
    return (
      <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
        <div className='flex items-start gap-3'>
          <AlertCircle className='h-5 w-5 text-red-600 mt-0.5 shrink-0' />
          <div className='flex-1'>
            <p className='text-red-700 font-medium'>Upload failed</p>
            <p className='text-sm text-red-600 mt-1'>Please try again</p>
          </div>
        </div>
        <Button
          type='button'
          onClick={handleReset}
          className='mt-3 w-full'
          variant='outline'
        >
          Try Again
        </Button>
      </div>
    );
  }

  if (uploadState === "uploading" && selectedFile) {
    return (
      <div className='p-4 border border-gray-200 rounded-lg space-y-3'>
        <div className='flex items-center gap-3'>
          <div className='animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full shrink-0' />
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium truncate'>{selectedFile.name}</p>
            <p className='text-xs text-muted-foreground mt-1'>
              Uploading to S3... {currentProgress}%
            </p>
          </div>
        </div>
        <Progress value={currentProgress} className='w-full' />
      </div>
    );
  }

  return (
    <div className='w-full'>
      <label
        htmlFor={`file-upload-${type}`}
        className='flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors'
      >
        <div className='flex flex-col items-center justify-center pt-5 pb-6'>
          <Upload className='h-10 w-10 text-gray-400 mb-3' />
          <p className='mb-2 text-sm text-gray-700'>
            <span className='font-semibold'>Click to upload</span> or drag and
            drop
          </p>
          <p className='text-xs text-gray-500'>
            {type === "audio" ? "Audio files" : "Video files"} (MAX 500MB)
          </p>
        </div>
        <input
          ref={fileInputRef}
          id={`file-upload-${type}`}
          type='file'
          className='hidden'
          accept={type === "audio" ? "audio/*" : "video/*"}
          onChange={handleFileSelect}
        />
      </label>
    </div>
  );
}
